"use string";

import jQuery from 'jquery';

/**
 * Repräsentation des Request-Objektes.
 *
 * @typedef gc.gis.util.GCRequest
 *
 * @property {*} context
 *      Instanz, die den Request ausgelöst hat
 *
 * @property {string} url
 *      Ajax-URL, ggf. mit Parametern {@link encodeURIComponent()}
 *
 * @property {boolean} [useOGCProxy=false]
 *      Flag, ob der Request über den OGC-ProxyService abgesetzt werden soll
 *
 * @property {string} [method='GET']
 *      <code>POST</code> oder <code>GET</code>;
 *      wird 1:1 an {@link jQuery.ajax()} weitergereicht.
 *
 * @property {Object<string,*>} [data]
 *      Assoziatives Parameter-Array (kann auch an die URL als Parameter gehängt
 *      werden). Wird 1:1 an {@link jQuery.ajax()} weitergereicht.
 *
 * @property {boolean} [cache=false]
 *      Flag, ob der Browser-Cache für den Request genutzt werden soll (Cache ist standardmäßig deaktiviert)
 *
 * @property {string} [dataType] Optionaler Parameter,  welcher festlegt, wie der Server-Response behandelt werden soll
 *      {@link gc.gis.util.GCRequestManagerDataType} z.B. gc.gis.util.GCRequestManagerDataType.JSON
 *
 * @property {string} [contentType] Optionaler Parameter, welcher den HTTP-Header Content-Type für den Request angibt
 *      (Standardwert: application/x-www-form-urlencoded). Bei Verwendung von POST-Requests kann damit der Typ des
 *      Request-Body gesetzt werden. Die automatische Serialisierung der Requestdaten passiert dann nicht.
 *      Siehe auch {@link gc.gis.util.GCRequestManagerContentType}.
 *
 * @property {function} [start]
 *      Optionale Callback, die beim Start des Requests ausgeführt wird.
 *      Geeignet z.B. für: Lade-Status-Start
 *
 * @property {function} success
 *      Callback, die bei Erfolg des Requests ausgeführt wird.
 *
 * @property {function} [error]
 *      Optionale Callback, die bei Miserfolg des Requests ausgeführt wird.
 *
 * @property {function} [finally]
 *      Optionale Callback, die in jedem Fall bei Beendigung des Requests
 *      ausgeführt wird. Geeignet z.B. für: Lade-Status-Stop
 * @property {string} [label]
 *      Optionale Meldung, die an {@link gc.gis.ui.GCUserInterface} weitergeleitet wird
 */


/**
 * Request Content-Typen.
 * Mögliche Werte für den HTTP-Header Content-Type eines Requests. Gibt das Format des Request-Body eines POST-Requests
 * an. Der Standardwert ist "application/x-www-form-urlencoded".
 *
 * @enum {string}
 */
gc.gis.util.GCRequestManagerContentType = {

    HTML: 'text/html',
    XML: 'text/xml',
    PLAIN: 'text/plain',
    JSON: 'application/json',
    OCTET_STREAM: 'application/octet-stream',
    PNG: 'image/png',
    JPG: 'image/jpg',
    GIF: 'image/gif'
};

/**
 * Response-Typen.
 * Gibt den in der Response erwarteten Datentype an und legt damit die weitere Verarbeitungsmethode fest.
 *
 * @enum {string}
 */
gc.gis.util.GCRequestManagerDataType = {

    HTML: 'html',
    XML: 'xml',
    PLAIN: 'text',
    JSON: 'json',
    JSONP: 'jsonp',
    SCRIPT: 'script'
};

/**
 * Request-Events.
 *
 * @enum {string}
 */
gc.gis.util.GCRequestManagerEvent = {
    /**
     * Vor dem Start.
     *
     * @event gc.gis.util.GCRequestManagerEvent#request:start
     * @property {*} context
     * @property {gc.gis.util.GCRequest} request
     */
    REQUEST_STARTED: 'request:start',
    /**
     * In jedem Fall nach Beendigung und nach (Mis-)Erfolg.
     *
     * @event gc.gis.util.GCRequestManagerEvent#request:stop
     * @property {*} context
     * @property {gc.gis.util.GCRequest} request
     */
    REQUEST_STOPPED: 'request:stop',
    /**
     * Bei Beendigung mit Erfolg.
     *
     * @event gc.gis.util.GCRequestManagerEvent#request:success
     * @property {*} context
     * @property {gc.gis.util.GCRequest} request
     * @property {*} data
     */
    REQUEST_SUCCESS: 'request:success',
    /**
     * Bei Beendigung mit Fehlschlag.
     *
     * @event gc.gis.util.GCRequestManagerEvent#request:error
     * @property {*} context
     * @property {gc.gis.util.GCRequest} request
     * @property {Error} error
     */
    REQUEST_ERROR: 'request:error'
};

/**
 * @classdesc
 * Wrapper für Ajax-Requests.
 * Kümmert sich um Fehlerbehandlung und stellt optionale Callbacks und Events
 * bereit.
 *
 * @example <caption>Beispielrequest</caption>
 * var myInstance = ...;
 *
 * gc.gis.iface.requests.ajax({
 *     // Meldung, die an {@link gc.gis.ui.GCUserInterface} weitergeleitet wird
 *     label: 'Lade irgendwas ... ',
 *
 *     // Context, mit dem die Callbacks aufgerufen werden (this)
 *     context: myInstance,
 *
 *     // URL (kann auch Querystring beinhalten -> kein 'data'-Parameter nötig)
 *     url: myInstance.getUrl(),
 *
 *     // Flag, ob der Request über den OGC-ProxyService abgesetzt werden soll
 *     useOGCProxy: false,
 *
 *     // 'GET' ist default und könnte auch weggelassen werden
 *     method: 'GET',
 *
 *     // Parameter. (optional, Querystring kann auch in 'url' enthalten sein)
 *     data: {
 *         foo: 'bar'
 *     },
 *
 *     // Cache standardmäßig deaktiviert
 *     cache: false,
 *
 *     // optional, legt fest, wie der Server-Response behandelt werden soll {@link gc.gis.util.GCRequestManagerDataType}
 *     dataType: gc.gis.util.GCRequestManagerDataType.JSON,
 *
 *     // optional, gibt den HTTP-Header Content-Type für den Request an (Standardwert: application/x-www-form-urlencoded),
 *     // Bei Verwendung von POST-Requests kann damit der Typ des Request-Body gesetzt werden. Die automatische
 *     // Serialisierung der Requestdaten passiert dann nicht
 *     // {@link gc.gis.util.GCRequestManagerContentType}
 *     contentType: abc,
 *
 *     // (optional) unterdrückt bei Bedarf das feuern von Events für den Erfolgsfall d.h.
 *     // {@link gc.gis.util.GCRequestManagerEvent.REQUEST_STARTED},
 *     // {@link gc.gis.util.GCRequestManagerEvent.REQUEST_SUCCESS) und
 *     // {@link gc.gis.util.GCRequestManagerEvent.REQUEST_STARTED)
 *     // dadurch erfolgt beispielsweise keine visuelle Information an den Nutzer über den aktuellen Request
 *     // das Fehler-Event {@link gc.gis.util.GCRequestManagerEvent.REQUEST_ERROR} ist davon unberührt
 *     silent: false,
 *
 *     // (optional) clientseitiger Abbruch eines Requests nicht in Log-Meldung überführen und kein Error-Event feuern
 *     abortSilent: false,
 *
 *     // (optional) Beim Start aufgerufen
 *     start: function() {
 *         myInstance.startLoadingAnimation();
 *     },
 *
 *     // Im Erfolgsfall aufgerufen
 *     success: function( data ) {
 *         myInstance.processData( data );
 *     },
 *
 *     // (optional) Im Fehlerfall aufgerufen (Achtung: Log-Meldung geht bereits an {@link gc.gis.log.GCLog})
 *     error: function( err ) {
 *         alert( err );
 *     },
 *
 *     // (optional) In jedem Fall nach <success|error> aufgerufen
 *     finally: function() {
 *         myInstance.stopLoadingAnimation();
 *     }
 * });
 *
 * @class
 * @memberof gc.gis.util
 * @author Anton Popov <a.popov@brain-scc.de>
 */
gc.gis.util.GCRequestManager = function()
{
    gc.gis.GCObservable.call( this );

    /**
     * Liste aktiver Requests.
     *
     * @protected
     * @type {gc.gis.util.GCRequest[]}
     */
    this._active_requests = [];
};
gc.gis.util.GCRequestManager.prototype = Object.create( gc.gis.GCObservable.prototype );

/**
 * Gibt die Liste der aktuell laufenden Requests zurück.
 *
 * @public
 * @method
 * @returns {gc.gis.util.GCRequest[]}
 */
gc.gis.util.GCRequestManager.prototype.getActiveProcesses = function()
{
    return this._active_requests;
};

/**
 * Gibt die Anzahl der aktuell laufenden Requests zurück.
 *
 * @public
 * @method
 * @returns {number}
 */
gc.gis.util.GCRequestManager.prototype.getActiveProcessCount = function()
{
    return this._active_requests.length;
};

/**
 * Führt einen Ajax-Request aus.
 *
 * @public
 * @method
 * @param {gc.gis.util.GCRequest} request
 * @return {jqXHR} jQuery XMLHttpRequest
 */
gc.gis.util.GCRequestManager.prototype.ajax = function(request)
{
    var self = this;

    this._onAjaxStart(request);

    var settings = {
        url: request.url,
        method: request.method || 'GET',
        data: request.data || null,
        cache: request.cache || false,
        success: function( data ) {
            try
            {
                self._onAjaxSuccess( request, data );
            }
            catch( err )
            {
                console.error(err);
                self._onAjaxError( request, err );
            }
            self._onAjaxStop( request );
        },
        error: function( jqXHR, textStatus, err ) {
            var msg = jqXHR.getResponseHeader('X-Error-Message');
            msg = msg ? msg + " (" + err + ")" : err;
            self._onAjaxError(request, msg);
            self._onAjaxStop( request );
        }
    };

    if (request.useOGCProxy)
    {
	    settings.url = gc.gis.GCUrl.buildUrl(gc.gis.iface.requests.ogcProxyUrl, {url: settings.url});
        settings.headers = {
		    "X-GC-Token": gc.gis.iface.requests.ogcProxyToken
	    };
    }
    if (request.dataType) {
        settings.dataType = request.dataType;
    }
    if (request.contentType && request.method && request.method.toUpperCase() === 'POST') {
        settings.contentType = request.contentType;
    }

    var jqXHR = jQuery.ajax(settings);

    // TODO ggf. Umstellung auf jqXHR Objekt (evtl. bei Versionsupgrade erforderlich)
    // jqXHR.done(function( data, textStatus, jqXHR ) { });
    // jqXHR.fail(function( jqXHR, textStatus, errorThrown ) { });
    // jqXHR.always(function( data|jqXHR, textStatus, jqXHR|errorThrown ) { });

    return jqXHR;
};

/**
 * Führt einen Request zum Herunterladen einer Datei aus.
 *
 * @public
 * @method
 * @param {string} url
 * @param {Object<string,string>} params
 */
gc.gis.util.GCRequestManager.prototype.download = function(url, params)
{
    // TODO ggf. Umstellung auf FileSaver => Vorteil Post kan per Ajax gesendet werden
    // https://github.com/eligrey/FileSaver.js/

	// temporäres Formular erzeugen, um Request als POST abzusetzen
	var form = jQuery('<form>',{
		action: url,
		method: 'post',
		target: '_blank'    // im Falle eines Fehlers wird das Kartenfenster dann nicht geschlossen
	});

	// Parameter zusammenfriemeln
	var keys = Object.keys(params);
	for(var i=0; i<keys.length; i++)
	{
		form.append(jQuery('<input>', {
			type: 'hidden',
			name: keys[i],
			value: params[keys[i]]
		}));
	}

	// Request absetzen
    // gc.gis.iface.getElement().append(form);
    // form.submit().remove();
    form.appendTo('body').submit().remove();
};

/**
 * Bricht den Request, welcher über die ajax-Methoder erzeugt wurde ab.
 *
 * @public
 * @method
 * @param {jqXHR} jqXHR jQuery XMLHttpRequest
 */
gc.gis.util.GCRequestManager.prototype.abort = function(jqXHR)
{
    jqXHR.abort();
};

/**
 * @protected
 * @param {gc.gis.util.GCRequest} request
 */
gc.gis.util.GCRequestManager.prototype._onAjaxStart = function( request )
{
    if( typeof request.start === 'function')
    {
        request.start.call( request.context );
    }
    this._active_requests.push( request );
    if (request.silent !== true)
    {
	    this._fireEvent( gc.gis.util.GCRequestManagerEvent.REQUEST_STARTED, {
		    context: this,
		    request: request
	    });
    }
};

/**
 * @protected
 * @param {gc.gis.util.GCRequest} request
 * @param {Error} err
 */
gc.gis.util.GCRequestManager.prototype._onAjaxError = function( request, err )
{
    var silent = (err === 'abort' && request.abortSilent === true);
    if (!silent)
    {
	    gc.gis.iface.log.error( this, err );
    }

    if( typeof request.error === 'function')
    {
        request.error.call( request.context, err );
    }

	if (!silent)
	{
		this._fireEvent(gc.gis.util.GCRequestManagerEvent.REQUEST_ERROR, {
			context: this,
			request: request,
			error: err
		});
	}
};

/**
 * @protected
 * @param {gc.gis.util.GCRequest} request
 * @param {string} data jQuery-Rückgabewert
 */
gc.gis.util.GCRequestManager.prototype._onAjaxSuccess = function( request, data )
{
    if( typeof request.success === 'function')
    {
        request.success.call( request.context, data );
    }
	if (request.silent !== true)
	{
		this._fireEvent(gc.gis.util.GCRequestManagerEvent.REQUEST_SUCCESS, {
			context: this,
			request: request,
			data: data
		});
	}
};

/**
 * @protected
 * @param {gc.gis.util.GCRequest} request
 */
gc.gis.util.GCRequestManager.prototype._onAjaxStop = function( request )
{
    if( typeof request.finally === 'function')
    {
        request.finally.call( request.context );
    }
    this._active_requests.splice( this._active_requests.indexOf( request ), 1 );
	if (request.silent !== true)
	{
		this._fireEvent(gc.gis.util.GCRequestManagerEvent.REQUEST_STOPPED, {
			context: this,
			request: request
		});
	}
};

